import '@unocss/reset/tailwind.css';
import 'element-plus/dist/index.css';
import 'element-plus/theme-chalk/dark/css-vars.css';
import 'uno.css';
import '../styles/index.css';

import ElementPlus from 'element-plus';
import type { ConfigProviderContext } from 'element-plus/es/components/config-provider/src/constants';
import ko from 'element-plus/es/locale/lang/ko';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { createPinia } from 'pinia';
import { createApp, defineComponent, h, type App } from 'vue';
import { RouterView } from 'vue-router';

import {
  COMPANY_GOOGLE_CALENDAR_ENABLED_KEY,
  COMPANY_SMART_HACCP_TOGGLE_ENABLED_KEY,
  DEPLOYMENT_MODE_KEY,
  LOGIN_DEFAULT_TENANT_CODE_KEY,
  LOGIN_EXTRA_TENANT_CODES_KEY,
} from '../composables/useDeploymentMode';
import {
  applyBranding,
  BRANDING_INJECTION_KEY,
  defaultBranding,
  resolveBrandingConfig,
} from '../branding';
import { attachInterceptors, getApiClient, setApiRouter } from '../api/client';
import { registerDirectives } from '../directives';
import { createAxRouter } from '../router';
import { ADMIN_LAYOUT_OVERLAY_KEY } from '../composables/adminLayoutOverlay';
import {
  SIDEBAR_DOCK_EXTRA_KEY,
  SIDEBAR_PINNED_MENU_CODES_KEY,
  SIDEBAR_PINNED_MENU_GROUP_CODES_KEY,
  SIDEBAR_PINNED_MENU_LAUNCHER_LABEL_KEY,
  SIDEBAR_PINNED_MENU_SECTION_LABEL_KEY,
} from '../composables/sidebarPinnedMenus';
import { SIDEBAR_MENU_SCOPE_CONFIG_KEY } from '../composables/sidebarMenuScope';
import { AX_TIER_DEFAULT_COLOR_PRESET } from '../branding/color-presets';
import { configureUiThemeDefaults, useUiThemeStore } from '../stores/uiTheme';
import type { AxAppOptions } from '../types/app';
import { installDocumentTitleSync } from '../branding/document-title';

function trimOrEmpty(value: string | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * AX-Admin core 앱 인스턴스 + 부속 객체.
 */
export interface AxAppContext {
  app: App<Element>;
  router: ReturnType<typeof createAxRouter>;
  pinia: ReturnType<typeof createPinia>;
}

/**
 * 루트 컴포넌트는 단순 RouterView. layouts/views가 라우트 트리에서 렌더된다.
 *
 * 주의: `h('router-view')`처럼 **문자열**로 넘기면 Vue는 커스텀 HTML 엘리먼트로
 * 렌더해 빈 화면이 된다. 반드시 `RouterView` 컴포넌트를 import해 `h(RouterView)`로
 * 전달해야 vue-router가 매칭된 라우트 컴포넌트를 실제로 그린다.
 */
const RootComponent = defineComponent({
  name: 'AxRoot',
  setup() {
    return () => h(RouterView);
  },
});

/**
 * createAxApp 팩토리.
 *
 * 출처: docs/01-architecture-overview.md §4.2.
 *
 * 본 차수 동작:
 * 1. Vue 앱 인스턴스 생성
 * 2. Pinia, Vue Router, Element Plus 등록 (UnoCSS reset/styles는 import side-effect)
 * 3. BrandingConfig provide + applyBranding (CSS 변수 + title/favicon)
 * 4. core-fe 디렉티브 (v-permission 등) 등록
 * 5. apiInterceptors 등록 (인스턴스는 다음 차수에서 별도 export)
 *
 * Tier 2/3/4 사용 예시는 docs §4.2 참조.
 */
export function createAxApp(options: AxAppOptions): AxAppContext {
  const branding = resolveBrandingConfig(options.branding ?? defaultBranding);

  const resolvedLoginTenantCode =
    trimOrEmpty(options.defaultLoginTenantCode) ||
    trimOrEmpty(import.meta.env.VITE_AX_DEFAULT_TENANT_CODE) ||
    'platform';

  const app = createApp(RootComponent);
  const pinia = createPinia();
  const router = createAxRouter({ extraRoutes: options.routes ?? [] });

  app.use(pinia);
  app.use(router);
  // Element Plus 패널·요일 문자열 + dayjs 월/년 헤더(예: 2026년 5월) 한국어
  dayjs.locale('ko');
  app.use(ElementPlus, { locale: ko } as unknown as ConfigProviderContext);

  // 배포 모드 / 브랜딩 주입
  app.provide(DEPLOYMENT_MODE_KEY, options.mode);
  app.provide(LOGIN_DEFAULT_TENANT_CODE_KEY, resolvedLoginTenantCode);
  app.provide(
    LOGIN_EXTRA_TENANT_CODES_KEY,
    (options.extraLoginTenantCodes ?? []).map((code) => code.trim()).filter(Boolean),
  );
  app.provide(COMPANY_GOOGLE_CALENDAR_ENABLED_KEY, options.enableCompanyGoogleCalendar ?? false);
  app.provide(COMPANY_SMART_HACCP_TOGGLE_ENABLED_KEY, options.enableSmartHaccpToggle ?? true);
  app.provide(BRANDING_INJECTION_KEY, branding);
  app.provide(SIDEBAR_PINNED_MENU_CODES_KEY, options.sidebarPinnedMenuCodes ?? []);
  app.provide(SIDEBAR_PINNED_MENU_GROUP_CODES_KEY, options.sidebarPinnedMenuGroupCodes ?? []);
  app.provide(
    SIDEBAR_PINNED_MENU_SECTION_LABEL_KEY,
    options.sidebarPinnedMenuSectionLabel?.trim() ?? '',
  );
  app.provide(
    SIDEBAR_PINNED_MENU_LAUNCHER_LABEL_KEY,
    options.sidebarPinnedMenuLauncherLabel?.trim() ?? '',
  );
  app.provide(ADMIN_LAYOUT_OVERLAY_KEY, options.adminLayoutOverlay ?? null);
  app.provide(SIDEBAR_DOCK_EXTRA_KEY, options.sidebarDockExtra ?? null);
  app.provide(SIDEBAR_MENU_SCOPE_CONFIG_KEY, options.sidebarMenuScope ?? null);

  // 디렉티브
  registerDirectives(app);

  // 브랜딩 런타임 적용 (CSS 변수, document.title, favicon)
  applyBranding(branding);
  // 탭 제목에 현재 메뉴 이름 (구포 #75 — "발주관리 - 구포국수 MES")
  installDocumentTitleSync(router, pinia);

  // UI 테마 복원 (R.19): localStorage 저장값 → DOM 즉시 반영.
  // app.use(pinia) 이후, store 생성 전에 Tier 기본 프리셋을 설정한다.
  configureUiThemeDefaults(options.defaultColorPreset ?? AX_TIER_DEFAULT_COLOR_PRESET.core);
  useUiThemeStore(pinia).init();

  // ---------------------------------------------------------------------------
  // API 클라이언트 셋업 (E.3)
  // ---------------------------------------------------------------------------
  // 1. router 를 먼저 client 모듈에 알린다 (401 redirect 용).
  // 2. getApiClient() 첫 호출 시 default 인터셉터(Bearer 첨부 + 401 처리)가
  //    pinia 활성 상태에서 부착된다 — 반드시 `app.use(pinia)` 이후여야 한다.
  // 3. 사용자가 추가로 inject 한 인터셉터는 default 다음에 부착되어 마지막 우선.
  setApiRouter(router);
  const apiClient = getApiClient();
  if (options.apiInterceptors && options.apiInterceptors.length > 0) {
    attachInterceptors(apiClient, options.apiInterceptors);
  }

  // TODO(next-phase): options.overrides — AxComponentRegistry 기반 컴포넌트 교체
  // TODO(next-phase): options.stores — 추가 Pinia store 자동 등록 헬퍼

  return { app, router, pinia };
}
